import React from 'react';

import { ContentItem } from '@/core/ambient-intelligence/types/ambient-intelligence.types';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';

// Props interface for inspect views
export interface InspectViewProps {
  content: ContentItem;
  onClose?: () => void;
  onAction?: (action: string, content: ContentItem, data?: any) => void;
}

// Default inspect view for unknown content types
const DefaultInspectView: React.FC<InspectViewProps> = ({ content, onClose }) => {
  return (
    <div className="inspect-theme-bg p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold inspect-theme-text mb-4">{content.title}</h1>

        {content.media?.hero && (
          <img
            src={appendImageRepoDomainIfNotExistInUrl(content.media.hero.url)}
            alt={content.media.hero.alt}
            className="w-full rounded-lg mb-6"
          />
        )}

        {content.description && <p className="inspect-theme-text-muted mb-6">{content.description}</p>}

        {content.tags && content.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {content.tags.map((tag, index) => (
              <span key={index} className="inspect-theme-tag px-3 py-1 rounded-full text-sm">
                {tag}
              </span>
            ))}
          </div>
        )}

        {content.data && (
          <div className="inspect-theme-bg-elevated rounded-lg p-4">
            <h3 className="font-medium inspect-theme-text mb-2">Details</h3>
            <pre className="text-sm inspect-theme-text-muted overflow-auto">
              {JSON.stringify(content.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * InspectViewRegistry - Maps content types to their detailed view components
 * Allows new inspect views without modifying the router
 */
class InspectViewRegistryClass {
  private views = new Map<string, React.ComponentType<InspectViewProps>>();

  /**
   * Register a new inspect view
   */
  register(contentType: string, view: React.ComponentType<InspectViewProps>): void {
    this.views.set(contentType, view);
    console.log(`✅ Registered inspect view: ${contentType}`);
  }

  /**
   * Get inspect view for content type
   */
  getView(contentType: string): React.ComponentType<InspectViewProps> {
    return this.views.get(contentType) || DefaultInspectView;
  }

  /**
   * Alias for getView for backward compatibility
   */
  get(contentType: string): React.ComponentType<InspectViewProps> {
    return this.getView(contentType);
  }

  /**
   * Render inspect view for content
   */
  render(content: ContentItem, props?: Omit<InspectViewProps, 'content'>): React.ReactElement {
    // First try to get view by content type
    let View = this.views.get(content.type);

    // If not found and content has a cardType in data, try that
    if (!View && content.data?.cardType) {
      View = this.views.get(content.data.cardType as string);
    }

    // Fall back to default view
    if (!View) {
      View = DefaultInspectView;
    }

    return <View content={content} {...props} />;
  }

  /**
   * Check if content type has inspect view
   */
  hasView(contentType: string): boolean {
    return this.views.has(contentType);
  }

  /**
   * List all registered content types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.views.keys());
  }

  /**
   * Unregister a content type
   */
  unregister(contentType: string): void {
    this.views.delete(contentType);
  }

  /**
   * Clear all registrations
   */
  clear(): void {
    this.views.clear();
  }
}

// Export singleton instance
export const InspectViewRegistry = new InspectViewRegistryClass();

// Export types
export type { InspectViewProps };
